import { Controller } from "@hotwired/stimulus"

// Live preview for the media library upload form.
//  - pick an image file  → preview via FileReader, set type=image/video
//  - paste an image URL   → preview the URL, set type=image
//  - paste a Vimeo URL    → fetch the oembed poster (Vimeo allows CORS),
//                           preview it, set type=video
export default class extends Controller {
  static targets = ["file", "url", "type", "preview", "placeholder"]

  connect() {
    this.applyTypeVisibility()
  }

  // Show/hide fields tagged with data-media-field="<type> [<type>…]" so the
  // form adapts to the selected media type (image / video / document).
  typeChanged() {
    this.applyTypeVisibility()
  }

  applyTypeVisibility() {
    const type = this.hasTypeTarget ? this.typeTarget.value : null
    this.element.querySelectorAll("[data-media-field]").forEach((el) => {
      const types = (el.dataset.mediaField || "").split(/\s+/).filter(Boolean)
      el.classList.toggle("hidden", Boolean(type) && types.length > 0 && !types.includes(type))
    })
  }

  fileChanged() {
    const file = this.hasFileTarget && this.fileTarget.files[0]
    if (!file) { return this.clear() }

    if (file.type.startsWith("video")) this.setType("video")
    else if (file.type.startsWith("image")) this.setType("image")

    if (!file.type.startsWith("image")) { return this.clear() } // can't preview a raw video file

    const reader = new FileReader()
    reader.onload = (e) => this.show(e.target.result)
    reader.readAsDataURL(file)
  }

  urlChanged() {
    // Debounce: don't hit Vimeo's API on every keystroke.
    clearTimeout(this._urlTimer)
    this._urlTimer = setTimeout(() => this._handleUrl(), 350)
  }

  _handleUrl() {
    const url = (this.hasUrlTarget ? this.urlTarget.value : "").trim()
    if (!url) { return this.clear() }

    if (/vimeo\.com/i.test(url)) {
      this.setType("video")
      this.loadVimeoPoster(url)
    } else if (/\.(png|jpe?g|gif|webp|avif|svg)(\?|#|$)/i.test(url)) {
      this.setType("image")
      this.show(url)
    } else {
      this.clear()
    }
  }

  async loadVimeoPoster(url) {
    try {
      const resp = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`)
      if (!resp.ok) return this.clear()
      const data = await resp.json()
      if (data.thumbnail_url) this.show(data.thumbnail_url)
      else this.clear()
    } catch (_) {
      this.clear() // network/CORS hiccup — the poster is fetched again server-side on save
    }
  }

  setType(value) {
    if (this.hasTypeTarget && this.typeTarget.value !== value) {
      this.typeTarget.value = value
      this.typeTarget.dispatchEvent(new Event("change", { bubbles: true }))
      this.applyTypeVisibility()
    }
  }

  show(src) {
    const img = document.createElement("img")
    img.src = src
    img.className = "max-h-56 rounded object-contain bg-base-200"
    img.alt = "preview"
    img.onerror = () => img.replaceWith(document.createTextNode("-"))

    this.previewTarget.replaceChildren(img)
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
  }

  clear() {
    if (this.hasPreviewTarget) this.previewTarget.innerHTML = ""
    if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
  }
}
